import * as React from 'react';

const uuidRegex =
  '[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}';

const pagesPaths = {
  home: '/home',
  'my-requests': '/my-requests',
  'talentCardManager': '/talent-card',
} as const;

export type PagesPathsKeys = keyof typeof pagesPaths;

export type Flags = Record<
  PagesPathsKeys,
  { active: boolean; skippable: boolean }
>;

type FlagsContextData = {
  flags: Flags;
  skipCheck: boolean;
};

const FlagsContext = React.createContext({} as FlagsContextData);

type FlagsProviderProps = {
  children: React.ReactNode;
  flags?: Partial<Flags>;
  skipCheck?: boolean;
};

export function FlagsProvider({
  children,
  flags,
  skipCheck = false,
}: FlagsProviderProps) {
  const providerValue = React.useMemo(
    () => ({
      flags: {
        ...Object.keys(pagesPaths).reduce(
          (acc, curr) => ({
            ...acc,
            // function flags default as false
            [curr]: { active: !curr.startsWith('func') },
          }),
          {} as Flags
        ),
        ...flags,
      },
      skipCheck,
    }),
    [flags, skipCheck]
  );

  return (
    <FlagsContext.Provider value={providerValue}>
      {children}
    </FlagsContext.Provider>
  );
}

export function useFlags() {
  const context = React.useContext(FlagsContext);

  if (!context) {
    throw new Error('useFlags must be used within a FlagsProvider');
  }

  return context.flags;
}

type MountFlagsOptions = {
  skipCheck?: boolean;
};

export function mountFlags(options: MountFlagsOptions = {}): Flags {
  let returnFlags = {
    home: {
      active: process.env.PAGE_HOME_ENABLED !== 'false',
      skippable: true,
    },
    'my-requests': {
      active: true,
      skippable: true,
    },
    'talentCardManager': {
      active: process.env.PAGE_TALENT_CARD_MANAGER_ENABLED !== 'false',
      skippable: true,
    },
  };

  // don't skip 'function' flags
  if (options.skipCheck) {
    returnFlags = {
      ...returnFlags,
      ...(Object.keys(pagesPaths)
        .filter(_i => {
          const key = _i as keyof typeof returnFlags;

          if (!returnFlags[key].skippable) {
            return false;
          }

          return !key.startsWith('func');
        })
        .reduce(
          (acc, curr) => ({
            ...acc,
            [curr]: { active: true },
          }),
          {}
        ) as Flags),
    };
  }

  return returnFlags;
}

type ShouldBlockAccessOptions = {
  skipCheck?: boolean;
};

export function shouldBlockAccess(
  path: string,
  options: ShouldBlockAccessOptions = {}
) {
  if (options.skipCheck) {
    return false;
  }

  const flags = mountFlags({ skipCheck: options.skipCheck });

  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(pagesPaths)) {
    if (typeof value === 'string' && path === value) {
      return !flags[key as PagesPathsKeys].active;
    }

    if (
      typeof value === 'string' &&
      new RegExp(uuidRegex).test(value) &&
      new RegExp(value).test(path)
    ) {
      return !flags[key as PagesPathsKeys].active;
    }
  }

  return false;
}
